import { AclAction, StoreKey } from "@common/constans";
import { StorageType } from "@common/enum";
import { AclPolicy, IDictionary, IMember, IPermission, IRole } from "@common/models";
import { ssoConfig } from "@config/ssoConfig";
import { Culture } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { store } from "@misc/Reducer";
import Storage from "./Storage";

export interface IPermissionCollection {
    [resource: string]: string[];
}

interface IStateResourceMap {
    [state: string]: IPermissionCollection;
}

export interface IAccess {
    [action: string]: boolean;
}

export type ICaptchaStatus = 'Loading' | 'Success' | 'Failed';

const codeKeyName: string = "auth-code";
const tokenKeyName: string = "auth-token";
const refreshTokenKeyName: string = "auth-refresh-token";
const idTokenKeyName: string = "auth-id-token";
const userKeyName: string = "userData";

export interface IAccount {
    stateResourceMap: IStateResourceMap;
    member: IMember;
    permissions: IPermissionCollection;
    defaultPolicy: AclPolicy;
    captchaStatus: ICaptchaStatus;
    getToken(): string;
    logout(): void;
    login(member: IMember): void;
    isGuest(): boolean;
    getUser(): IMember;
    setCode(code: string): void;
    setToken(token: string): void;
    setRefreshToken(token: string): void;
    setIdToken(token: string): void;
    setCaptchaStatus(status: ICaptchaStatus): void;
    getCode(): string;
    getToken(): string;
    getRefreshToken(): string;
    getIdToken(): string;
    getCaptchaStatus(): ICaptchaStatus;
    isAllowed(resource: string, action: string): boolean;
    hasAccessToState(state: number): boolean;
    getAccessList(resource: string, ...actions: string[]): IAccess;
    registerPermissions(state: number, permissions?: IPermissionCollection): void;
    setDefaultPolicy(policy: AclPolicy): void;
    updatePermissions(): void;
}

export const Account: IAccount = () => { };
Account.stateResourceMap = {};
Account.member = {};
Account.defaultPolicy = AclPolicy.Deny;
Account.permissions = {};
Account.captchaStatus = 'Loading';

Account.login = (member: IMember) => {
    Account.member = member;
    Storage.set(userKeyName, member);
    Account.updatePermissions();
    store.dispatch<IAppAction>({ type: AppAction.User, payload: { user: Account.member } });
}

Account.logout = () => {
    Storage.remove(tokenKeyName);
    Storage.remove(refreshTokenKeyName);
    Storage.remove(idTokenKeyName);
    Storage.remove(userKeyName);
    Storage.remove(codeKeyName);
    store?.dispatch<IAppAction>({ type: AppAction.User, payload: { user: {} } });
    store?.dispatch<IAppAction>({ type: AppAction.ContactAddresses, payload: { contactAddresses: [] } });
}

Account.isGuest = (): boolean => {
    return !(Account.member && Account.member.guid);
}

Account.getUser = (): IMember => {
    return Account.member;
}

Account.setCode = (code: string) => {
    Storage.set(codeKeyName, code);
}

Account.setToken = (token: string) => {
    Storage.set(tokenKeyName, token);
}

Account.setRefreshToken = (token: string) => {
    Storage.set(refreshTokenKeyName, token);
}

Account.setIdToken = (token: string) => {
    Storage.set(idTokenKeyName, token);
}

Account.setCaptchaStatus = (status: ICaptchaStatus) => {
    Account.captchaStatus = status;
}

Account.getCode = (): string => {
    return Storage.get<string>(codeKeyName);
}

Account.getToken = (): string => {
    return Storage.get<string>(tokenKeyName);
}

Account.getRefreshToken = (): string => {
    return Storage.get<string>(refreshTokenKeyName);
}

Account.getIdToken = (): string => {
    return Storage.get<string>(idTokenKeyName);
}

Account.getCaptchaStatus = (): ICaptchaStatus => {
    return Account.captchaStatus;
}

Account.isAllowed = (resource: string, action: string): boolean => {
    const userPermissions = Account.permissions;
    const userActions = userPermissions[resource];
    if ((userActions && (userActions.indexOf("*") >= 0 || userActions.indexOf(action) >= 0)) ||
        // tslint:disable-next-line:max-line-length
        (userPermissions["*"] && (userPermissions["*"].indexOf("*") >= 0 || userPermissions["*"].indexOf(action) >= 0))) {
        return true;
    }
    return Account.defaultPolicy.toString === AclPolicy.Allow.toString;
}

/**
 * Check if user has access to all actions of all resources
 */
Account.hasAccessToState = (state: number): boolean => {
    if (!state) { return true; }
    const requiredPermissions = Account.stateResourceMap[state];
    if (!requiredPermissions) { return Account.defaultPolicy.toString === AclPolicy.Allow.toString; }
    for (let resources = Object.keys(requiredPermissions), i = resources.length; i--;) {
        const resource = resources[i];
        const actions = requiredPermissions[resource];
        for (let j = actions.length; j--;) {
            if (!Account.isAllowed(resource, actions[j])) { return false; }
        }
    }
    return true;
}

/**
* Check if user has access to the action of resource
*/
Account.getAccessList = (resource: string, ...actions: string[]): IAccess => {
    if (!actions.length) {
        actions = [AclAction.Read, AclAction.Add, AclAction.Edit, AclAction.Delete];
    }
    const access: IDictionary<any> = {};
    for (let i = actions.length; i--;) {
        if (Account.isAllowed(resource, actions[i])) { access[actions[i]] = true; }
    }
    return access;
}

/**
* Same states will overwrite each others
*/
Account.registerPermissions = (state: number, permissions?: IPermissionCollection) => {
    Account.stateResourceMap[state] = permissions || {};
}

Account.setDefaultPolicy = (policy: AclPolicy) => {
    Account.defaultPolicy = policy;
}

Account.updatePermissions = () => {
    Account.permissions = {};
    const role = {} as IRole;
    if (role && role.permissions) {
        for (let k = role.permissions.length; k--;) {
            const permission = role.permissions[k] as IPermission;
            if (permission.resource && permission.action) {
                if (!(permission.resource in Account.permissions)) {
                    Account.permissions[permission.resource] = [];
                }
                Account.permissions[permission.resource].push(permission.action);
            }
        }
    }
}

try {
    Account.member = Storage.get(userKeyName);
    if (!Account.member) { Account.member = {}; }
    Account.updatePermissions();
} catch (e) {
    Account.logout();
}

// export const redirectToSSO = () => {
//     const url = `${window.location.origin}/${Culture.getLocale().lang}${Path.Login}`;
//     clearInterval(loginInterval);
//     windowObject = createWindow(url, "Login", { resizable: 0 });
//     loginInterval = setInterval(() => {
//         if (windowObject?.closed) {
//             store.dispatch<IAppAction>({ type: AppAction.RefreshTokenError, payload: { refreshTokenError: false } });
//             clearInterval(loginInterval);
//             const auth = Account;
//             const redirectUri = Config.get("redirectUrl");
//             const code = auth.getCode();
//             store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: true } });
//             authenticate(Action.POST, { data: { code, redirectUri } })
//                 .then((response) => {
//                     const authenticate = response as any;
//                     auth.setToken(authenticate.access_token);
//                     auth.setRefreshToken(authenticate.refresh_token);
//                     auth.setIdToken(authenticate.id_token);
//                     return member(Action.GET);
//                 }).then((response) => {
//                     const member = response.content;
//                     auth.login(member);
//                 }).catch((error) => {
//                     auth.logout();
//                 }).finally(() => {
//                     store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: false } });
//                 });
//         }
//     }, 1000);
// }

// export const redirectToLogout = () => {
//     const url = `${window.location.origin}/${Culture.getLocale().lang}${Path.Logout}`;
//     clearInterval(logoutInterval);
//     windowObject = createWindow(url, "Logout", { resizable: 0 });
//     logoutInterval = setInterval(() => {
//         if (windowObject?.closed) {
//             clearInterval(logoutInterval);
//             Account.logout();
//             store.dispatch<IAppAction>({ type: AppAction.User, payload: { user: {} } })
//         }
//     }, 1000);
// }

export const redirectToLogin = () => {
    Storage.set(StoreKey.LastRoute, window.location.pathname.slice(3) + window.location.search, StorageType.Session);
    setTimeout(() => {
        window.location.href = ssoConfig(Culture.getLocale().lang).loginUrl;
    }, 150)
}

export const redirectToLogout = () => {
    window.location.href = ssoConfig(Culture.getLocale().lang).logoutUrl;
    // Account.logout();
}

